import { useEffect, useState } from "react";

import {
  deleteSimulatedConnector,
  ensureSimulatedConnector,
  getSimulatedFeedStatus,
  replaceSimulatedConnectorCsv,
  startSimulatedFeed,
  type SimulatedFeedStatus,
} from "@/lib/api/building";

/** Session cache — avoids re-probing every closed tile (and the 404 noise that caused). */
const presence_by_equipment = new Map<string, boolean>();

function cache_key(buildingId: string, equipmentId: string): string {
  return `${buildingId}:${equipmentId}`;
}

/**
 * @param probe — when true, hit Building status (open/edit card). When false, only
 *   read the session cache so closed tiles don't spam 404 for non-simulated equipment.
 */
export function useSimulatedConnector(
  buildingId: string,
  equipmentId: string,
  enabled: boolean,
  probe: boolean,
) {
  const key = cache_key(buildingId, equipmentId);
  const cached = presence_by_equipment.get(key);
  const [status, set_status] = useState<SimulatedFeedStatus | null | undefined>(
    () => {
      if (cached === true) {
        return { connector_id: equipmentId, running: false };
      }
      if (cached === false) return null;
      return undefined;
    },
  );
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    if (!probe) {
      const known = presence_by_equipment.get(key);
      if (known === true) {
        set_status(
          (prev) => prev ?? { connector_id: equipmentId, running: false },
        );
      } else if (known === false) {
        set_status(null);
      }
      return;
    }

    // Already confirmed absent this session — don't re-404 on every open.
    if (presence_by_equipment.get(key) === false) {
      set_status(null);
      return;
    }

    let cancelled = false;
    getSimulatedFeedStatus(buildingId, equipmentId)
      .then((s) => {
        if (cancelled) return;
        presence_by_equipment.set(key, s != null);
        set_status(s);
      })
      .catch(() => {
        if (cancelled) return;
        presence_by_equipment.set(key, false);
        set_status(null);
      });
    return () => {
      cancelled = true;
    };
  }, [buildingId, equipmentId, enabled, probe, key]);

  const has_connector =
    status != null || presence_by_equipment.get(key) === true;

  async function start_after_csv() {
    const next = await startSimulatedFeed(buildingId, equipmentId);
    presence_by_equipment.set(key, true);
    set_status(next);
  }

  async function replace_csv(file: File) {
    set_busy(true);
    set_error(null);
    try {
      await replaceSimulatedConnectorCsv(buildingId, equipmentId, file);
      await start_after_csv();
    } catch {
      set_error("CSV:n vaihto epäonnistui.");
      throw new Error("CSV:n vaihto epäonnistui.");
    } finally {
      set_busy(false);
    }
  }

  async function enable_with_csv(file: File) {
    set_busy(true);
    set_error(null);
    try {
      await ensureSimulatedConnector(buildingId, equipmentId, file);
      presence_by_equipment.set(key, true);
      await start_after_csv();
    } catch {
      set_error("Simuloinnin käyttöönotto epäonnistui.");
      throw new Error("Simuloinnin käyttöönotto epäonnistui.");
    } finally {
      set_busy(false);
    }
  }

  async function disable() {
    if (!has_connector) return;
    set_busy(true);
    set_error(null);
    try {
      await deleteSimulatedConnector(buildingId, equipmentId);
      presence_by_equipment.set(key, false);
      set_status(null);
    } catch {
      set_error("Simuloinnin poisto epäonnistui.");
      throw new Error("Simuloinnin poisto epäonnistui.");
    } finally {
      set_busy(false);
    }
  }

  return {
    status,
    busy,
    error,
    has_connector,
    /** Still resolving presence while the edit card is open. */
    loading: probe && status === undefined,
    replace_csv,
    enable_with_csv,
    disable,
  };
}

/** Used after ensure so sibling tiles can show Simuloitu without probing. */
export function markSimulatedConnectorPresent(
  buildingId: string,
  equipmentId: string,
): void {
  presence_by_equipment.set(cache_key(buildingId, equipmentId), true);
}

export function markSimulatedConnectorAbsent(
  buildingId: string,
  equipmentId: string,
): void {
  presence_by_equipment.set(cache_key(buildingId, equipmentId), false);
}
